import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import SEO from '../../common/SEO';
import { getCanonicalUrl } from '../../../utils/seo';

const discoverCastleImage =
  'https://belvoircastlepull.b-cdn.net/Website/CJP%20Belvoir%20Castle-234%20(1).webp';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const PolicyContent = styled.div`
  background-color: white;
  padding: 3rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 2rem;
  text-align: center;
`;

const IntroText = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 2rem;
  text-align: center;
  font-weight: 500;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  margin: 2rem 0 1rem 0;
`;

const BodyText = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.1rem;
  line-height: 1.7;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const BulletList = styled.ul`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.1rem;
  line-height: 1.7;
  color: ${props => props.theme.colors.primary};
  margin: 1rem 0 1rem 2rem;
`;

const BulletItem = styled.li`
  margin-bottom: 0.5rem;
`;

const HighlightBox = styled.div`
  background-color: rgba(218, 165, 32, 0.1);
  border-left: 4px solid ${props => props.theme.colors.accent};
  padding: 1.5rem;
  margin: 2rem 0;
  border-radius: 0 8px 8px 0;
`;

const ResourceList = styled.ul`
  list-style: none;
  margin: 1rem 0 0;
  padding: 0;
`;

const ResourceItem = styled.li`
  margin-bottom: 0.75rem;
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.1rem;
  line-height: 1.6;
`;

const ExternalLink = styled.a`
  color: ${props => props.theme.colors.accent};
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const InternalLink = styled(Link)`
  color: ${props => props.theme.colors.accent};
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const EventSafety = () => {
  return (
    <PageContainer>
      <SEO
        title="Event Safety Information | Belvoir Castle"
        description="Visitor safety information for large events at Belvoir Castle, including Forbidden Forest. Learn about the Protect UK #BeSafeBeSound campaign and practical guidance for your visit."
        keywords="Belvoir Castle event safety, Forbidden Forest, BeSafeBeSound, Protect UK, visitor safety, festival safety"
        canonicalUrl={getCanonicalUrl('your-visit/event-safety')}
        ogImage={discoverCastleImage}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Event Safety Information',
          description:
            'Visitor safety information for large events at Belvoir Castle, including the Protect UK #BeSafeBeSound campaign.',
          url: getCanonicalUrl('your-visit/event-safety'),
        }}
      />
      <ParallaxBanner
        title="Event Safety"
        subtitle="Visitor information for large events"
        image={discoverCastleImage}
        imagePosition="44% 58%"
      />

      <ContentContainer>
        <PolicyContent>
          <PageTitle>Event Safety Information</PageTitle>

          <IntroText>
            Your safety and wellbeing are our priority. As part of our event planning, we are
            supporting the Protect UK #BeSafeBeSound campaign.
          </IntroText>

          <BodyText>
            The campaign provides clear and practical advice to help visitors feel informed,
            reassured and confident when attending events. The information is designed to support
            attendees, not to cause concern.
          </BodyText>

          <SectionTitle>Forbidden Forest at Belvoir Estate</SectionTitle>
          <BodyText>
            Belvoir Castle will host <strong>Forbidden Forest</strong>, a large-scale music festival,
            from <strong>Thursday 4 to Sunday 7 June 2026</strong>. The festival takes place on the
            Belvoir Estate and is organised independently of Belvoir Castle visitor operations.
          </BodyText>
          <BodyText>
            If you are attending Forbidden Forest, please visit the{' '}
            <ExternalLink
              href="https://www.forbidden-forest.co.uk/"
              target="_blank"
              rel="noopener noreferrer"
            >
              official Forbidden Forest website
            </ExternalLink>{' '}
            for tickets, lineup and festival-specific information.
          </BodyText>

          <SectionTitle>Protect UK #BeSafeBeSound</SectionTitle>
          <BodyText>
            We are proud to support the Protect UK #BeSafeBeSound campaign, developed by Counter
            Terrorism Policing in partnership with the UK&apos;s live music industry. The campaign
            encourages everyone to stay alert, look out for one another, and report anything that
            doesn&apos;t feel right.
          </BodyText>

          <HighlightBox>
            <BodyText style={{ margin: 0 }}>
              Please check this page and our social media channels for the latest visitor information
              as the event approaches. We will share practical guidance on travel, access and estate
              arrangements closer to the festival dates.
            </BodyText>
          </HighlightBox>

          <SectionTitle>Practical guidance for visitors</SectionTitle>
          <BodyText>
            Whether you are attending Forbidden Forest or visiting Belvoir Castle and the estate
            during a large event, the following advice may help you plan ahead:
          </BodyText>
          <BulletList>
            <BulletItem>
              Allow extra time for your journey — roads around the estate may be busier than usual
            </BulletItem>
            <BulletItem>
              Follow all event signage and instructions from stewards, security and estate staff
            </BulletItem>
            <BulletItem>
              Keep your belongings secure and be aware of your surroundings in crowded areas
            </BulletItem>
            <BulletItem>
              If you see something that doesn&apos;t feel right, tell a member of staff or security
              immediately. In an emergency, call 999
            </BulletItem>
            <BulletItem>
              Check our{' '}
              <InternalLink to="/your-visit/opening-times">opening times</InternalLink> and{' '}
              <InternalLink to="/your-visit/directions">directions</InternalLink> before you travel
            </BulletItem>
          </BulletList>

          <SectionTitle>Official campaign resources</SectionTitle>
          <BodyText>
            For further safety advice and free campaign materials, please visit the official Protect
            UK and Counter Terrorism Policing resources:
          </BodyText>
          <ResourceList>
            <ResourceItem>
              <ExternalLink
                href="https://www.counterterrorism.police.uk/musictoolkit/"
                target="_blank"
                rel="noopener noreferrer"
              >
                #BeSafeBeSound media toolkit
              </ExternalLink>{' '}
              — campaign wording, graphics and digital assets
            </ResourceItem>
            <ResourceItem>
              <ExternalLink
                href="https://www.counterterrorism.police.uk/safetyadvice/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Festival and venue safety advice
              </ExternalLink>{' '}
              — detailed guidance for event attendees
            </ResourceItem>
            <ResourceItem>
              <ExternalLink
                href="https://www.protectuk.police.uk/"
                target="_blank"
                rel="noopener noreferrer"
              >
                ProtectUK
              </ExternalLink>{' '}
              — national protective security advice and resources
            </ResourceItem>
          </ResourceList>

          <SectionTitle>Stay updated</SectionTitle>
          <BodyText>
            We will share further visitor information on this page and across our social media
            channels as Forbidden Forest approaches. If you have questions about visiting Belvoir
            Castle during the event weekend, please contact us at{' '}
            <ExternalLink href="mailto:info@belvoircastle.com">info@belvoircastle.com</ExternalLink>{' '}
            or call <ExternalLink href="tel:+441476871001">01476 871001</ExternalLink>.
          </BodyText>
        </PolicyContent>
      </ContentContainer>

      <Footer />
    </PageContainer>
  );
};

export default EventSafety;
